import React from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const SEGMENT_TOOLTIPS: Record<string, string> = {
  "Emerging Defence": "Little or no evidence of established Defence entrenchment — the company appears to be entering the Defence supply chain. This is the core profile Radar exists to surface and can still be a high-priority (P1) opportunity.",
  "Developing Defence": "Some Defence entrenchment is building (early contracts, relationships or capability), but the company is not yet an established supplier. A growing prospect worth close analyst attention.",
  "Established Defence": "Already well-embedded in Defence (e.g. long Defence history or organisation-stated DISP membership). Routed to the Established Account Pathway for human review, not automatic contact.",
  "Prime/Mature": "A prime contractor or Tier-1-scale, mature supplier. Routed to the human-led Established Account Pathway rather than the emerging-SME pipeline; not a new-entrant opportunity.",
};

const SEGMENT_CONCEPT = "Entrenchment Segment shows how embedded a company already is in Defence. It's a separate axis from the P1–P4 priority and is never subtracted from the Opportunity Score — it routes the human review, not the score.";

/* ---- Evidence class visual system (verified facts NEVER look like inferences) ---- */
const EVIDENCE_STYLES: Record<string, string> = {
  "Verified Source": "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold",
  "Prospect-Confirmed": "bg-blue-50 text-blue-800 border-blue-300 font-semibold",
  "Analyst-Verified": "bg-teal-50 text-teal-800 border-teal-300 font-medium",
  "Self-Attested": "bg-violet-50 text-violet-700 border-dashed border-violet-300",
  "Inferred": "bg-indigo-50 text-indigo-700 border-dashed border-indigo-300 italic",
  "No Evidence Identified": "bg-slate-100 text-slate-500 border-slate-200",
  "Stale": "bg-orange-50 text-orange-600 border-orange-200 line-through",
  "Discovery Hypothesis": "bg-indigo-50 text-indigo-700 border-dashed border-indigo-300 italic",
  "Research Required": "bg-amber-50 text-amber-800 border-amber-300",
  "Prospect-confirmed": "bg-blue-50 text-blue-800 border-blue-300 font-semibold",
  "Evidence-backed": "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold",
};

const GATE_STYLES: Record<string, string> = {
  "Review Ready": "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold",
  "Validate Data": "bg-amber-50 text-amber-800 border-amber-300 font-semibold",
  "Research Required": "bg-amber-50 text-amber-700 border-amber-200",
  "Research": "bg-slate-100 text-slate-600 border-slate-200",
  "Nurture": "bg-blue-50 text-blue-700 border-blue-200",
  "Established Account Pathway": "bg-teal-50 text-teal-800 border-teal-300 font-semibold",
  "Do Not Contact": "bg-red-50 text-red-700 border-red-300 font-semibold",
};

const PRIORITY_STYLES: Record<string, string> = {
  P1: "bg-red-50 text-red-700 border-red-300 font-bold",
  P2: "bg-amber-50 text-amber-800 border-amber-300 font-semibold",
  P3: "bg-blue-50 text-blue-700 border-blue-200 font-medium",
  P4: "bg-slate-100 text-slate-600 border-slate-200",
  Exclude: "bg-slate-50 text-slate-400 border-slate-200",
};

const SEGMENT_STYLES: Record<string, string> = {
  "Emerging Defence": "bg-teal-50 text-teal-700 border-teal-200",
  "Developing Defence": "bg-blue-50 text-blue-700 border-blue-200",
  "Established Defence": "bg-slate-800 text-white border-slate-800",
  "Prime/Mature": "bg-slate-900 text-white border-slate-900",
};

const PRIORITY_TITLES: Record<string, string> = {
  P1: "P1 — Immediate Review band (80–100)",
  P2: "P2 — Strong Prospect band (65–79)",
  P3: "P3 — Nurture band (50–64)",
  P4: "P4 — Low Priority band (30–49)",
  Exclude: "Exclude — below threshold (under 30)",
};
const PRIORITY_TIP: Record<string, string> = {
  P1: "The highest-opportunity band; becomes \u201CReview Ready\u201D only once confidence, a current signal, confirmed entity and human approval are all met.",
  P2: "A strong opportunity that requires analyst review before any contact.",
  P3: "Moderate opportunity; keep warm, no automatic contact.",
  P4: "Weak signal; monitor for change.",
  Exclude: "Retained with a reason, not actively worked.",
};
const PRIORITY_CONCEPT = "Priority is the band derived from the Radar Opportunity Score (0\u2013100). A company sits in exactly one band; it's about \u201Chow strong\u201D, not \u201Cact now\u201D.";

const GATE_TIP: Record<string, string> = {
  "Review Ready": "All P1 gates satisfied; ready for a human-led DefencIQ discovery conversation.",
  "Validate Data": "Opportunity looks strong but evidence confidence or a gate is short; verify the data before review.",
  "Research Required": "Key attributable evidence is missing; research before progressing. Absence of evidence is a research state, not a gap.",
  "Research": "Early-stage or low-signal candidate; gather more evidence.",
  "Nurture": "Keep warm; no automatic contact.",
  "Established Account Pathway": "Mature/established Defence account; route to human-led established-account handling, not the emerging-SME pipeline.",
  "Do Not Contact": "Active suppression in place; outreach is not permitted.",
};
const GATE_CONCEPT = "The Action Gate is the recommended safe next step for a human, derived from score, evidence confidence, gates and segment \u2014 separate from the priority band.";

const EVIDENCE_TIP: Record<string, string> = {
  "Verified Source": "Corroborated by an attributable, authoritative source; treat as a verified fact.",
  "Prospect-Confirmed": "Confirmed by the prospect themselves (e.g. in a discovery call).",
  "Analyst-Verified": "Reviewed and validated by an analyst.",
  "Self-Attested": "Stated by the company about itself; not independently verified.",
  "Inferred": "An Analyst / AI Discovery Hypothesis, not a verified fact; treat as a lead to test.",
  "No Evidence Identified": "Nothing found yet; a research state, never proof of a gap or non-membership.",
  "Stale": "Previously valid but past its freshness window; re-check before relying on it.",
  "Discovery Hypothesis": "An Analyst / AI Discovery Hypothesis, not a verified fact; treat as a lead to test.",
  "Research Required": "Key attributable evidence is missing; research before progressing. Absence is not a gap.",
  "Prospect-confirmed": "Security need confirmed by the prospect themselves (e.g. in a discovery call).",
  "Evidence-backed": "Security opportunity supported by attributable evidence such as a contract or assessment.",
};
const EVIDENCE_CONCEPT = "Evidence class shows how trustworthy an item is. Verified facts are never presented like inferences.";

const TRIGGER_TIP = "A current, high-confidence, verified trigger event with human approval, overlaid on a P1/P2 company. Signals \u201Cact now\u201D; it's an overlay, not a priority band.";

function Chip({ label, className, testid }: { label: string; className: string; testid?: string }) {
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs whitespace-nowrap transition-colors",
        className
      )}
    >
      {label}
    </span>
  );
}

function TooltipChip({ label, className, title, description, concept, testid }:
  { label: string; className: string; title: string; description?: string; concept?: string; testid?: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid={testid} className="cursor-help inline-flex">
            <Chip label={label} className={className} />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-slate-900 text-slate-50 leading-relaxed" data-testid={testid ? `${testid}-tooltip` : undefined}>
          <p className="font-semibold mb-1">{title}</p>
          {description && <p className="mb-1.5">{description}</p>}
          {concept && <p className="text-[10px] text-slate-300 border-t border-slate-700 pt-1.5">{concept}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const EvidenceBadge = ({ value, testid }: { value: string; testid?: string }) => (
  <TooltipChip label={value} testid={testid}
    className={EVIDENCE_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200"}
    title={value} description={EVIDENCE_TIP[value]} concept={EVIDENCE_CONCEPT} />
);

export const GateBadge = ({ value, testid }: { value: string; testid?: string }) => (
  <TooltipChip label={value} testid={testid}
    className={GATE_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200"}
    title={value} description={GATE_TIP[value]} concept={GATE_CONCEPT} />
);

export const PriorityBadge = ({ value, testid }: { value: string; testid?: string }) => (
  <TooltipChip label={value} testid={testid}
    className={PRIORITY_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200"}
    title={PRIORITY_TITLES[value] || value} description={PRIORITY_TIP[value]} concept={PRIORITY_CONCEPT} />
);

export const SegmentBadge = ({ value, testid }: { value: string; testid?: string }) => (
  <TooltipChip label={value} testid={testid}
    className={SEGMENT_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200"}
    title={value} description={SEGMENT_TOOLTIPS[value]} concept={SEGMENT_CONCEPT} />
);

export const VerifiedTriggerBadge = ({ compact, testid }: { compact?: boolean; testid?: string }) => (
  <TooltipChip label={compact ? "Trigger" : "Verified Trigger Event"} testid={testid}
    className="bg-red-50 text-red-600 border-red-200 font-bold uppercase !text-[10px] px-1"
    title="Verified Trigger Event" description={TRIGGER_TIP} />
);

export function ScoreBar({ value, max = 100, kind = "opportunity", testid }: { value: number; max?: number; kind?: string; testid?: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color =
    kind === "confidence"
      ? value >= 70 ? "bg-emerald-600" : value >= 45 ? "bg-amber-500" : "bg-red-500"
      : kind === "entrenchment"
      ? value >= 70 ? "bg-indigo-700" : value >= 45 ? "bg-indigo-600" : value >= 20 ? "bg-indigo-400" : "bg-slate-400"
      : value >= 80 ? "bg-teal-700" : value >= 65 ? "bg-teal-600" : value >= 50 ? "bg-blue-600" : value >= 30 ? "bg-slate-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="h-2 w-full max-w-[120px] rounded-full bg-slate-100 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-300", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="num text-xs font-semibold text-slate-700 w-8 text-right">{value}</span>
    </div>
  );
}

export function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}
